import React, { useEffect, useMemo, useState } from 'react';

import { v4 as uuid } from 'uuid';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';

import { useBatchedBulkDeleteVoucherUsers } from '~/hooks/apis/promo/useBulkDeleteVoucherUsers';

import { type SelectedVoucherUser, createVoucherUserBulkBatches } from './voucherUserBulkBatches';

type BatchStatus = 'queued' | 'loading' | 'previewed' | 'success' | 'failed';
type Batch = {
  id: number;
  voucherId: string;
  userIds: Array<string>;
  eligibleUserIds: Array<string>;
  skippedCount: number;
  deletedCount: number;
  status: BatchStatus;
  error?: string;
};

type Props = {
  selected: Array<SelectedVoucherUser>;
  batchSize: number;
  onFinished: () => Promise<void>;
  onSelectionLockChange: (locked: boolean) => void;
  onClearSelection: () => void;
};

function createBatches(selected: Array<SelectedVoucherUser>, batchSize: number): Array<Batch> {
  return createVoucherUserBulkBatches(selected, batchSize).map((batch, index) => ({
    ...batch,
    id: index + 1,
    eligibleUserIds: [],
    skippedCount: 0,
    deletedCount: 0,
    status: 'queued',
  }));
}

export default function VoucherUserBulkActions({
  selected,
  batchSize,
  onFinished,
  onSelectionLockChange,
  onClearSelection,
}: Props) {
  const deleteMutation = useBatchedBulkDeleteVoucherUsers();
  const [action, setAction] = useState<'delete' | 'expiry'>('delete');
  const [batches, setBatches] = useState<Array<Batch>>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  useEffect(() => {
    if (selected.length === 0 && !isRunning) {
      setBatches([]);
      setError('');
      setCompleted(false);
      setRefreshError('');
      setConfirmOpen(false);
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }
  }, [selected.length, isRunning]);

  const eligibleCount = batches.reduce((count, batch) => count + batch.eligibleUserIds.length, 0);
  const deletedCount = batches.reduce((count, batch) => count + batch.deletedCount, 0);
  const skippedCount = batches.reduce((count, batch) => count + batch.skippedCount, 0);

  const updateBatch = (id: number, update: Partial<Batch>) => {
    setBatches((current) => current.map((batch) => (batch.id === id ? { ...batch, ...update } : batch)));
  };

  const preview = async () => {
    const planned = createBatches(selected, batchSize);
    if (planned.length === 0) return;
    setError('');
    setRefreshError('');
    setCompleted(false);
    setBatches([...planned]);
    setIsRunning(true);
    onSelectionLockChange(true);
    const batchId = uuid();
    try {
      for (let index = 0; index < planned.length; index += 1) {
        const batch = planned[index];
        updateBatch(batch.id, { status: 'loading' });
        const response = await deleteMutation.mutateAsync({
          voucher_id: batch.voucherId,
          voucher_users: batch.userIds.map((user_id) => ({ user_id })),
          dryRun: true,
          batchId,
          batchRunCountTotal: planned.length,
          batchRunCount: batch.id,
        });
        const previewed: Batch = {
          ...batch,
          eligibleUserIds: response.result.willDelete.map((user) => user.userId),
          skippedCount: response.result.skipped.length,
          status: 'previewed',
        };
        planned[index] = previewed;
        updateBatch(batch.id, previewed);
      }
      setConfirmOpen(true);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Preview failed';
      const failed = planned.find((batch) => batch.status === 'queued' || batch.status === 'loading');
      if (failed) updateBatch(failed.id, { status: 'failed', error: message });
      setError(`Preview stopped: ${message}`);
      onSelectionLockChange(false);
    } finally {
      setIsRunning(false);
    }
  };

  const execute = async () => {
    setConfirmOpen(false);
    setError('');
    setRefreshError('');
    setIsRunning(true);
    const executable = batches.filter((batch) => batch.eligibleUserIds.length > 0);
    const batchId = uuid();
    let completedCount = 0;
    let stopped = false;
    try {
      for (let index = 0; index < executable.length; index += 1) {
        const batch = executable[index];
        updateBatch(batch.id, { status: 'loading' });
        try {
          const response = await deleteMutation.mutateAsync({
            voucher_id: batch.voucherId,
            voucher_users: batch.eligibleUserIds.map((user_id) => ({ user_id })),
            dryRun: false,
            batchId,
            batchRunCountTotal: executable.length,
            batchRunCount: index + 1,
          });
          updateBatch(batch.id, {
            status: 'success',
            deletedCount: response.result.deletedVoucherUsers.length,
            skippedCount: batch.skippedCount + response.result.skipped.length,
          });
          completedCount += response.result.deletedVoucherUsers.length;
        } catch (cause) {
          const message = cause instanceof Error ? cause.message : 'Delete failed';
          updateBatch(batch.id, { status: 'failed', error: message });
          setError(
            `Deletion stopped on batch ${index + 1} of ${executable.length} after deleting ${completedCount} voucher users: ${message}`,
          );
          stopped = true;
          break;
        }
      }
    } finally {
      setCompleted(!stopped);
      try {
        await onFinished();
      } catch (cause) {
        setRefreshError(
          `The list could not refresh: ${cause instanceof Error ? cause.message : 'Unknown error'}. Refresh the list before making further changes.`,
        );
      }
      onSelectionLockChange(false);
      setIsRunning(false);
    }
  };

  const columns = useMemo<Array<GridColDef<Batch>>>(
    () => [
      { field: 'id', headerName: 'Batch', width: 90 },
      { field: 'voucherId', headerName: 'Voucher ID', flex: 1, minWidth: 210 },
      { field: 'requested', headerName: 'Selected', width: 100, valueGetter: (_, row) => row.userIds.length },
      { field: 'eligible', headerName: 'Eligible', width: 100, valueGetter: (_, row) => row.eligibleUserIds.length },
      { field: 'deletedCount', headerName: 'Deleted', width: 100 },
      { field: 'skippedCount', headerName: 'Skipped', width: 100 },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        renderCell: ({ row }) =>
          row.status === 'loading' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography component="span" variant="body2">
                Working
              </Typography>
            </Stack>
          ) : (
            <Typography color={row.status === 'failed' ? 'error' : 'text.primary'} title={row.error}>
              {row.status}
            </Typography>
          ),
      },
    ],
    [],
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Bulk actions · {selected.length} selected</Typography>
          <Button
            variant="text"
            onClick={onClearSelection}
            disabled={isRunning || confirmOpen || selected.length === 0}
          >
            Clear selection
          </Button>
        </Stack>
        <RadioGroup row value={action} onChange={(event) => setAction(event.target.value as typeof action)}>
          <FormControlLabel value="delete" control={<Radio />} label="Delete selected" disabled={isRunning} />
          <FormControlLabel value="expiry" control={<Radio />} label="Update expiry" disabled />
        </RadioGroup>
        <Typography variant="body2" color="text.secondary">
          Select all applies to the current table page. Selected rows remain selected when you move between pages.
          Requests run one page-sized batch at a time.
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={() => void preview()}
            disabled={isRunning || selected.length === 0 || action !== 'delete'}
          >
            Preview deletion
          </Button>
        </Box>
        {batches.length > 0 && (
          <DataGrid
            rows={batches}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{ backgroundColor: 'background.paper' }}
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {completed && !isRunning && (
          <Alert severity="info">
            Deleted {deletedCount} voucher users. {skippedCount} were skipped.
            {!refreshError && ' The list has been refreshed.'}
          </Alert>
        )}
        {refreshError && <Alert severity="warning">{refreshError}</Alert>}
      </Stack>
      <Dialog
        open={confirmOpen}
        onClose={
          isRunning
            ? undefined
            : () => {
                setConfirmOpen(false);
                onSelectionLockChange(false);
              }
        }
      >
        <DialogTitle>Delete selected voucher users?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete up to {eligibleCount} of {selected.length} selected voucher users.{' '}
            {skippedCount} are ineligible. Eligibility is checked again during deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              setConfirmOpen(false);
              onSelectionLockChange(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void execute()} disabled={eligibleCount === 0 || isRunning}>
            Delete {eligibleCount}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
